// src/shared/bonus-scheme.js — the bonus scheme, in ONE place (v130.17).
//
// Zero dependencies so both the staff app and any future server-side use can
// import it and can never disagree about somebody's bonus.
//
// THE SCHEME (from the March 2026 posters, verified against all 18 published
// rows across the Sales Assistant and Store Manager tables):
//
//   variable rate = role rate x (performance / 100)   — only at 80% or above
//   fixed rate    = role rate in full                 — only at 100% or above
//   total         = variable + fixed
//
// So a Store Manager (10%) at 90% earns 9.0% and nothing fixed; at 100% they
// earn 10% + 10% = 20%. Below 80% there is no bonus at all. Above 120% it
// keeps scaling — Matt confirmed no cap (18 Aug), matching "up to 10%+".
//
// PUBLISHED-TABLE ROUNDING: the posters show a Sales Assistant at 110% as
// 8.2%, where the exact figure is 8.25% (round-half-EVEN). Money is always
// computed from the EXACT rate; displayRate() exists only so the on-screen
// percentage matches the poster a manager has on the wall.

export const BONUS_THRESHOLD_VARIABLE = 80;   // below this: nothing
export const BONUS_THRESHOLD_FIXED = 100;     // the fixed half is a cliff, not a ramp

// Rate by role. Matt, 18 Aug: relief managers are paid at the store-manager
// rate; area managers are not in this scheme.
export const BONUS_RATES = [
  { match: /area/i,                    rate: null, label: 'Area Manager' },
  { match: /sales\s*assistant/i,       rate: 7.5,  label: 'Sales Assistant' },
  { match: /manager|in[\s-]*charge/i,  rate: 10,   label: 'Store Manager' },
];

// Pure: staff role → { rate, label } | { rate: null } when the scheme does not
// cover them. Order matters: area is tested before manager.
export function rateForRole(role) {
  const raw = String(role || '').trim();
  if (!raw) return { rate: null, label: null };
  // `staff.role` is FREE TEXT on prod — "Sales Assistant", "sales_assistant",
  // "store_manager", "Store in-charge" all occur. Normalise separators to
  // spaces BEFORE matching, or an underscore silently drops somebody out of
  // the scheme and the app tells them they have no bonus.
  const r = raw.toLowerCase().replace(/[_-]+/g, ' ');
  for (const entry of BONUS_RATES) {
    if (entry.match.test(r)) return { rate: entry.rate, label: entry.label };
  }
  return { rate: null, label: raw };
}

// Pure: the scheme itself. performance is a percentage (e.g. 96.4).
// Returns exact rates — never rounded — plus the money when a salary is given.
export function calcBonus(rate, performance, monthlySalary) {
  const r = Number(rate);
  const p = Number(performance);
  if (!(r > 0) || !isFinite(p)) return null;
  const variableRate = p >= BONUS_THRESHOLD_VARIABLE ? (r * p) / 100 : 0;
  const fixedRate = p >= BONUS_THRESHOLD_FIXED ? r : 0;
  const totalRate = variableRate + fixedRate;
  const salary = Number(monthlySalary);
  const money = (salary > 0)
    ? { variable: (salary * variableRate) / 100, fixed: (salary * fixedRate) / 100, total: (salary * totalRate) / 100 }
    : null;
  return { performance: p, variableRate, fixedRate, totalRate, money, earning: totalRate > 0 };
}

// Round half-to-EVEN at 1dp, so the on-screen percentage matches the printed
// poster (which shows 8.25 as 8.2). Display only — never used for money.
export function displayRate(x) {
  const v = Number(x) * 10;
  const f = Math.floor(v);
  const d = v - f;
  if (Math.abs(d - 0.5) < 1e-9) return ((f % 2 === 0) ? f : f + 1) / 10;
  return Math.round(v) / 10;
}

// The published ladder for a role, for showing the same table as the poster.
export function bonusLadder(rate, steps) {
  const marks = steps || [80, 85, 90, 95, 100, 105, 110, 115, 120];
  return marks.map((p) => {
    const b = calcBonus(rate, p, 0);
    return { performance: p, variableRate: b.variableRate, fixedRate: b.fixedRate, totalRate: b.totalRate };
  });
}
